import { ref, reactive } from 'vue'
import { useToast } from 'primevue/usetoast'

const undoStack = reactive([])
const isUndoing = ref(false)

export function useUndo() {
  const toast = useToast()

  const addUndoAction = (action) => {
    if (isUndoing.value) return
    
    undoStack.push({
      ...action,
      id: Date.now(),
      timestamp: new Date()
    })
    
    // Limiter la pile d'undo à 10 actions
    if (undoStack.length > 10) {
      undoStack.shift()
    }

    // Afficher le toast d'undo
    showUndoToast(action)
  }

  const undo = async () => {
    if (undoStack.length === 0) return

    const action = undoStack.pop()
    isUndoing.value = true

    try {
      await action.undoAction()
      
      toast.add({
        severity: 'success',
        summary: 'Cancelled action',
        detail: `${action.description} was cancelled`,
        life: 3000
      })
    } catch (error) {
      console.error('Erreur while cancelling', error)
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible to cancel this action',
        life: 5000
      })
      
      // Remettre l'action dans la pile en cas d'échec
      undoStack.push(action)
    } finally {
      isUndoing.value = false
    }
  }

  const showUndoToast = (action) => {
    toast.add({
      severity: 'info',
      summary: action.description,
      detail: 'Action succeeded',
      life: 5000,
      closable: true,
      group: 'undo',
      content: {
        component: 'UndoToast',
        props: {
          message: action.description,
          onUndo: () => undo()
        }
      }
    })
  }

  const clearUndoStack = () => {
    undoStack.splice(0, undoStack.length)
  }

  const canUndo = () => {
    return undoStack.length > 0 && !isUndoing.value
  }

  return {
    addUndoAction,
    undo,
    clearUndoStack,
    canUndo,
    undoStack,
    isUndoing
  }
}